import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { LOGO_URL } from "./brand";

const brl = (n) => (n || 0).toLocaleString("pt-BR", { style:"currency", currency:"BRL" });
const monthLabel = (m) => {
  if (!m) return "";
  const [y, mm] = m.split("-");
  const names = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
  return `${names[Number(mm)-1]} / ${y}`;
};

async function drawHeader(doc, title, month) {
  // Try to add logo
  try {
    const res = await fetch(LOGO_URL);
    const blob = await res.blob();
    const dataUrl = await new Promise((resolve) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result);
      r.readAsDataURL(blob);
    });
    doc.addImage(dataUrl, "PNG", 14, 10, 22, 22);
  } catch {}
  doc.setFont("helvetica", "bold"); doc.setTextColor(123, 86, 21);
  doc.setFontSize(20); doc.text("Arya Studio", 42, 20);
  doc.setFont("helvetica", "normal"); doc.setTextColor(122, 105, 66);
  doc.setFontSize(9); doc.text("Onde cuidado e beleza se encontram.", 42, 26);

  doc.setDrawColor(199,154,58); doc.setLineWidth(0.4);
  doc.line(14, 36, 196, 36);

  doc.setTextColor(42, 33, 19); doc.setFontSize(14); doc.setFont("helvetica","bold");
  doc.text(title, 14, 46);
  if (month) {
    doc.setFontSize(10); doc.setFont("helvetica","normal"); doc.setTextColor(122, 105, 66);
    doc.text(`Período: ${monthLabel(month)}`, 14, 52);
  }
  doc.setTextColor(0,0,0);
}

function drawFooter(doc) {
  const pages = doc.internal.getNumberOfPages();
  for (let i=1; i<=pages; i++) {
    doc.setPage(i);
    doc.setFontSize(8); doc.setTextColor(150,132,90);
    doc.text(`Arya Studio · Relatório emitido em ${new Date().toLocaleString("pt-BR")} · Página ${i} de ${pages}`,
             105, 290, { align:"center" });
  }
}

export async function generateFinanceReport({ month, summary, entries }) {
  const doc = new jsPDF();
  await drawHeader(doc, "Relatório Financeiro", month);

  autoTable(doc, {
    startY: 58,
    theme: "plain",
    styles: { fontSize: 11 },
    head: [["Indicador", "Valor"]],
    headStyles: { fillColor: [251,235,201], textColor: [123,86,21], fontStyle:"bold" },
    body: [
      ["Entradas de serviços (agenda)", brl(summary.appointments_income)],
      ["Outras entradas manuais", brl(summary.manual_income)],
      ["Total de entradas", brl(summary.total_income)],
      ["Despesas", brl(summary.expense)],
      ["Lucro líquido", brl(summary.profit)],
    ],
  });

  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 10,
    head: [["Data", "Tipo", "Categoria", "Descrição", "Valor"]],
    headStyles: { fillColor: [251,235,201], textColor: [123,86,21] },
    styles: { fontSize: 9 },
    body: (entries||[]).map(e => [
      e.date,
      e.type === "income" ? "Entrada" : "Despesa",
      e.category,
      e.description || "-",
      (e.type === "expense" ? "- " : "+ ") + brl(e.amount),
    ]),
  });

  drawFooter(doc);
  doc.save(`arya-financeiro-${month || "geral"}.pdf`);
}

export async function generateStaffReport({ month, rows }) {
  const doc = new jsPDF();
  await drawHeader(doc, "Relatório de Equipe & Comissões", month);

  autoTable(doc, {
    startY: 58,
    head: [["Profissional", "Função", "Atendimentos", "Bruto", "Comissão %", "A pagar"]],
    headStyles: { fillColor: [251,235,201], textColor: [123,86,21] },
    styles: { fontSize: 10 },
    body: (rows||[]).map(o => [
      o.staff.name,
      o.staff.role || "-",
      o.count,
      brl(o.gross),
      `${o.staff.commission_pct || 0}%`,
      brl(o.commission_amount),
    ]),
    foot: [[
      { content: "TOTAL", colSpan: 3, styles: { fontStyle:"bold", halign:"right" }},
      { content: brl((rows||[]).reduce((s,r)=>s+(r.gross||0),0)), styles: { fontStyle:"bold" }},
      "",
      { content: brl((rows||[]).reduce((s,r)=>s+(r.commission_amount||0),0)), styles: { fontStyle:"bold" }},
    ]],
    footStyles: { fillColor: [253,246,220], textColor: [42,33,19] },
  });

  drawFooter(doc);
  doc.save(`arya-equipe-${month || "geral"}.pdf`);
}

export async function generateAgendaReport({ date, appointments }) {
  const doc = new jsPDF();
  const label = new Date(date + "T12:00:00").toLocaleDateString("pt-BR", { weekday:"long", day:"2-digit", month:"long", year:"numeric" });
  await drawHeader(doc, "Agenda do dia", null);
  doc.setFontSize(11); doc.setTextColor(122, 105, 66);
  doc.text(label.charAt(0).toUpperCase() + label.slice(1), 14, 52);
  doc.setTextColor(0,0,0);

  autoTable(doc, {
    startY: 58,
    head: [["Horário", "Cliente", "Telefone", "Serviço", "Duração", "Valor", "Status"]],
    headStyles: { fillColor: [251,235,201], textColor: [123,86,21] },
    styles: { fontSize: 9 },
    body: (appointments||[]).map(a => [
      new Date(a.start_at).toLocaleTimeString("pt-BR", { hour:"2-digit", minute:"2-digit" }),
      a.user_name || "-",
      a.user_phone || "-",
      a.service_title,
      `${a.duration_min} min`,
      brl(a.price),
      a.status,
    ]),
  });

  drawFooter(doc);
  doc.save(`arya-agenda-${date}.pdf`);
}

export async function generatePlansReport({ plans }) {
  const doc = new jsPDF();
  await drawHeader(doc, "Planos Ativos & Cobranças", null);

  const active = plans.filter(p => p.active);
  const paid = active.filter(p => p.status === "paga").length;
  const overdue = active.filter(p => p.status === "atrasada").length;
  const monthly = active.reduce((s,p)=> s + (p.price || 0), 0);

  autoTable(doc, {
    startY: 58,
    theme: "plain",
    head: [["Indicador", "Valor"]],
    headStyles: { fillColor: [251,235,201], textColor: [123,86,21], fontStyle:"bold" },
    styles: { fontSize: 11 },
    body: [
      ["Planos ativos", String(active.length)],
      ["Em dia (pagas)", String(paid)],
      ["Em atraso", String(overdue)],
      ["Receita esperada", brl(monthly)],
    ],
  });

  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 10,
    head: [["Cliente", "Plano", "Valor", "Próx. cobrança", "Status", "Telefone"]],
    headStyles: { fillColor: [251,235,201], textColor: [123,86,21] },
    styles: { fontSize: 9 },
    body: plans.map(p => [
      p.client_name, p.plan_type, brl(p.price), p.next_billing,
      p.status === "paga" ? "PAGA" : "ATRASADA",
      p.phone || "-",
    ]),
  });

  drawFooter(doc);
  doc.save(`arya-planos.pdf`);
}
